import { getProjects as getStaticProjects } from '../data/projects';
import { applyPlatformActivity } from './platformStats';
import { supabase, isDevelopmentMode } from '../utils/supabase/client';
import supabaseSync from './supabaseSync';

/**
 * Service pour gérer les projets avec Supabase
 */

// Récupérer tous les projets (version legacy pour compatibilité)
// Le catalogue (src/data/projects.js) donne les fiches. Avec Supabase, le montant collecté et le nombre
// d'investisseurs viennent de la table projects, calculés par la base à partir des paiements confirmés.
export const getAllProjects = async () => {
  const projects = getStaticProjects();
  try {
    if (isDevelopmentMode) {
      // Démonstration : investissements confirmés enregistrés dans ce navigateur
      return applyPlatformActivity(projects);
    }
    const { data, error } = await supabase
      .from('projects')
      .select('site_code, raised_amount, investors_count')
      .not('site_code', 'is', null);
    if (error) throw error;
    const live = Object.fromEntries((data || []).map(row => [row.site_code, row]));
    return projects.map(project => (live[project.id]
      ? { ...project, montantRecu: Number(live[project.id].raised_amount) || 0, membres: Number(live[project.id].investors_count) || 0 }
      : project));
  } catch (error) {
    // Base injoignable : le catalogue s'affiche quand même, avec ses montants d'origine
    console.error('Erreur lors de la récupération des projets:', error.message);
    return projects;
  }
};

// Récupérer un projet par son ID (version legacy)
export const getProjectById = async (id) => {
  try {
    const projects = getStaticProjects();
    const project = projects.find(p => p.id === id);
    return project || null;
  } catch (error) {
    console.error(`Erreur lors de la récupération du projet ${id}:`, error.message);
    return null;
  }
};

// ===================== RÉCUPÉRATION DES PROJETS =====================

export const getProjects = async (userId = null) => {
  try {
    // Utiliser le service de synchronisation
    const result = await supabaseSync.getAllProjects(userId);
    return result.data || [];
  } catch (error) {
    console.error('Erreur lors de la récupération des projets:', error);
    // Fallback sur localStorage
    return JSON.parse(localStorage.getItem('projects') || '[]');
  }
};

export const getUserProjects = async (userId) => {
  try {
    const result = await supabaseSync.getAllProjects(userId);
    return result.data ? result.data.filter(p => p.creator_id === userId) : [];
  } catch (error) {
    console.error('Erreur récupération projets utilisateur:', error);
    const localProjects = JSON.parse(localStorage.getItem('projects') || '[]');
    return localProjects.filter(p => p.userId === userId);
  }
};

// ===================== CRÉATION/MODIFICATION DE PROJETS =====================

export const createProject = async (projectData, userId) => {
  try {
    const projectToCreate = {
      ...projectData,
      creator_id: userId,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      montant_recu: 0,
      membres: 0,
      // Mapper les champs si nécessaire
      titre: projectData.titre || projectData.title,
      cout_total: projectData.coutTotal || projectData.cout_total || 0,
      image_url: projectData.imageUrl || projectData.image_url,
      video_url: projectData.videoUrl || projectData.video_url,
      taux_retour: projectData.tauxRetour || projectData.taux_retour || 0.05
    };

    const result = await supabaseSync.syncProject(projectToCreate, 'insert');
    
    if (result.success) {
      // Déclencher événement global pour rafraîchir l'UI
      window.dispatchEvent(new CustomEvent('project-created', { 
        detail: result.data 
      }));
      return result.data;
    } else if (result.queued) {
      // Projet en attente de synchronisation
      console.log('Projet créé hors ligne, synchronisation en attente');
      return projectToCreate;
    } else {
      throw new Error(result.error);
    }
  } catch (error) {
    console.error('Erreur lors de la création du projet:', error);
    return null;
  }
};

export const updateProject = async (projectId, projectData) => {
  try {
    const projectToUpdate = {
      ...projectData,
      id: projectId,
      updated_at: new Date().toISOString()
    };

    const result = await supabaseSync.syncProject(projectToUpdate, 'update');
    
    if (result.success) {
      window.dispatchEvent(new CustomEvent('project-updated', { 
        detail: result.data 
      }));
      return result.data;
    } else if (result.queued) {
      console.log('Projet modifié hors ligne, synchronisation en attente');
      return projectToUpdate;
    } else {
      throw new Error(result.error);
    }
  } catch (error) {
    console.error('Erreur lors de la modification du projet:', error);
    return null;
  }
};

// ===================== STATISTIQUES DES PROJETS =====================

export const getProjectStats = async (userId = null) => {
  try {
    let query = supabase
      .from('projects')
      .select('cout_total, montant_recu, membres');
    
    if (userId) {
      query = query.eq('creator_id', userId);
    }

    const { data, error } = await query;
    
    if (error) throw error;

    const stats = data.reduce((acc, project) => ({
      totalProjects: acc.totalProjects + 1,
      totalFunding: acc.totalFunding + (project.cout_total || 0),
      totalRaised: acc.totalRaised + (project.montant_recu || 0),
      totalMembers: acc.totalMembers + (project.membres || 0)
    }), {
      totalProjects: 0,
      totalFunding: 0,
      totalRaised: 0,
      totalMembers: 0
    });

    return stats;
  } catch (error) {
    console.error('Erreur récupération statistiques:', error);
    // Fallback sur données locales
    const localProjects = JSON.parse(localStorage.getItem('projects') || '[]');
    const filteredProjects = userId ? 
      localProjects.filter(p => p.userId === userId) : 
      localProjects;
    
    return filteredProjects.reduce((acc, project) => ({
      totalProjects: acc.totalProjects + 1,
      totalFunding: acc.totalFunding + (project.coutTotal || 0),
      totalRaised: acc.totalRaised + (project.montantRecu || 0),
      totalMembers: acc.totalMembers + (project.membres || 0)
    }), {
      totalProjects: 0,
      totalFunding: 0,
      totalRaised: 0,
      totalMembers: 0
    });
  }
};

// ===================== RECHERCHE ET FILTRAGE =====================

export const searchProjectsInDB = async (searchTerm, filters = {}) => {
  try {
    let query = supabase
      .from('projects')
      .select('*');

    // Recherche textuelle
    if (searchTerm) {
      query = query.or(`titre.ilike.%${searchTerm}%, description.ilike.%${searchTerm}%`);
    }

    // Filtres
    if (filters.category) {
      query = query.eq('category', filters.category);
    }
    
    if (filters.minFunding) {
      query = query.gte('cout_total', filters.minFunding);
    }
    
    if (filters.maxFunding) {
      query = query.lte('cout_total', filters.maxFunding);
    }

    if (filters.status) {
      if (filters.status === 'funded') {
        query = query.gte('montant_recu', supabase.raw('cout_total'));
      } else if (filters.status === 'active') {
        query = query.lt('montant_recu', supabase.raw('cout_total'));
      }
    }

    query = query.order('created_at', { ascending: false });

    const { data, error } = await query;
    
    if (error) throw error;
    
    return data;
  } catch (error) {
    console.error('Erreur recherche projets:', error);
    // Fallback sur recherche locale
    const localProjects = JSON.parse(localStorage.getItem('projects') || '[]');
    return localProjects.filter(project => {
      if (searchTerm) {
        const searchLower = searchTerm.toLowerCase();
        const titleMatch = project.titre?.toLowerCase().includes(searchLower);
        const descMatch = project.description?.toLowerCase().includes(searchLower);
        if (!titleMatch && !descMatch) return false;
      }
      return true;
    });
  }
};

// ===================== FONCTIONS UTILITAIRES =====================

export const calculateProjectProgress = (project) => {
  const montantRecu = project.montant_recu || project.montantRecu || 0;
  const coutTotal = project.cout_total || project.coutTotal || 0;
  
  if (coutTotal === 0) return 0;
  
  return Math.round((montantRecu / coutTotal) * 100);
};

export const isProjectFullyFunded = (project) => {
  return calculateProjectProgress(project) >= 100;
};

export const getProjectRemainingAmount = (project) => {
  const montantRecu = project.montant_recu || project.montantRecu || 0;
  const coutTotal = project.cout_total || project.coutTotal || 0;
  
  return Math.max(0, coutTotal - montantRecu);
};



// Supprimer un projet
export const deleteProject = async (id) => {
  try {
    const projects = getStaticProjects();
    const updatedProjects = projects.filter(p => p.id !== id);
    
    // Sauvegarder dans localStorage
    localStorage.setItem('projects', JSON.stringify(updatedProjects));
    
    return true;
  } catch (error) {
    console.error(`Erreur lors de la suppression du projet ${id}:`, error.message);
    return false;
  }
};

// Rechercher des projets
export const searchProjects = async (query) => {
  try {
    const projects = getStaticProjects();
    const lowercaseQuery = query.toLowerCase();
    
    return projects.filter(p => 
      p.titre.toLowerCase().includes(lowercaseQuery) || 
      p.description.toLowerCase().includes(lowercaseQuery)
    );
  } catch (error) {
    console.error('Erreur lors de la recherche de projets:', error.message);
    return [];
  }
};

// Initialiser les projets par défaut si nécessaire
export const initializeDefaultProjects = async (defaultProjects) => {
  try {
    // Vérifier si des projets existent déjà dans localStorage
    const stored = localStorage.getItem('projects');
    if (!stored || JSON.parse(stored).length === 0) {
      // Si aucun projet n'existe, initialiser avec les projets par défaut
      localStorage.setItem('projects', JSON.stringify(defaultProjects));
      console.log('✅ Projets par défaut initialisés avec succès');
    }
    
    return true;
  } catch (error) {
    console.error('Erreur lors de l\'initialisation des projets par défaut:', error.message);
    return false;
  }
};